export const TaxStructuringBenefits = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 text-gray-900">
            Structure Your Deal to Minimize Income Tax Impact
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Working directly with a buyer gives you flexibility to structure the transaction in ways that can significantly reduce your tax burden
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/a5d3746e2fc7f4a295258b46e9c39041.jpg"
              alt="Tax structuring strategies for apartment building sale"
              className="w-full h-full rounded-lg shadow-xl object-cover"
            />
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-6 text-gray-900">
              Why Tax Planning Matters in Apartment Building Sales
            </h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              When you sell an apartment building, you're potentially facing substantial capital gains taxes, depreciation recapture, and state income taxes. Without proper planning, you could lose 30-40% or more of your sale proceeds to taxes.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The advantage of selling for sale by owner to a direct buyer is the flexibility to structure the deal in tax-advantaged ways. Traditional buyers with conventional financing have limited flexibility, but experienced investors can work with you to create structures that minimize your tax impact.
            </p>
            
            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-money-dollar-circle-line text-green-600"></i>
                Real Tax Savings Example
              </h4>
              <p className="text-gray-700 mb-2">
                <strong>Sale Price:</strong> $3,000,000<br />
                <strong>Original Purchase:</strong> $1,500,000<br />
                <strong>Depreciation Taken:</strong> $400,000
              </p>
              <p className="text-gray-700 mt-3">
                <strong>Traditional Sale Tax:</strong> ~$520,000<br />
                <strong>Structured Sale Tax:</strong> ~$180,000<br />
                <strong>Tax Savings:</strong> <span className="text-green-600 font-bold">$340,000</span>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-2xl font-bold mb-8 text-gray-900 text-center">
            Tax-Advantaged Deal Structures We Can Offer
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border-2 border-teal-200 rounded-lg p-6 hover:border-teal-400 transition-colors">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-exchange-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">1031 Exchange Facilitation</h4>
                  <p className="text-gray-700">
                    We can structure the sale to accommodate a 1031 exchange, allowing you to defer all capital gains taxes by reinvesting in another property. We work with your qualified intermediary to ensure proper timing and documentation.
                  </p>
                </div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-700">
                  <strong>Tax Benefit:</strong> Defer 100% of capital gains taxes indefinitely
                </p>
              </div>
            </div>

            <div className="border-2 border-teal-200 rounded-lg p-6 hover:border-teal-400 transition-colors">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Installment Sale Structure</h4>
                  <p className="text-gray-700">
                    Instead of receiving all cash at closing, we can structure seller financing where you receive payments over time. This spreads your capital gains over multiple years, potentially keeping you in lower tax brackets.
                  </p>
                </div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-700">
                  <strong>Tax Benefit:</strong> Spread tax liability over 5-15 years, reduce annual tax burden
                </p>
              </div>
            </div>

            <div className="border-2 border-teal-200 rounded-lg p-6 hover:border-teal-400 transition-colors">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-pie-chart-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Partial Interest Sale</h4>
                  <p className="text-gray-700">
                    We can purchase a partial interest in your property initially, with options to buy the remainder later. This allows you to recognize gains gradually and maintain some income from the property during transition.
                  </p>
                </div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-700">
                  <strong>Tax Benefit:</strong> Control timing of gain recognition, maintain cash flow
                </p>
              </div>
            </div>

            <div className="border-2 border-teal-200 rounded-lg p-6 hover:border-teal-400 transition-colors">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Opportunity Zone Investment</h4>
                  <p className="text-gray-700">
                    If your property or our replacement properties are in Qualified Opportunity Zones, we can structure deals that provide significant tax benefits including deferral and potential elimination of some capital gains.
                  </p>
                </div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-700">
                  <strong>Tax Benefit:</strong> Defer taxes until 2026, reduce gains by up to 15%
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-8 md:p-12">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-3xl text-white"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">
                Additional Tax Planning Strategies
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Timing Optimization</h4>
                  <p className="text-gray-700 mb-4">
                    We can close in the tax year that's most advantageous for you, whether that's before year-end or in the following year, depending on your overall tax situation.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Cost Segregation Benefits</h4>
                  <p className="text-gray-700 mb-4">
                    If you've done cost segregation studies, we can structure the sale to maximize the benefits of accelerated depreciation you've already taken.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Entity Structure Planning</h4>
                  <p className="text-gray-700 mb-4">
                    Depending on how you hold the property (LLC, partnership, personal), we can structure the transaction to optimize tax treatment.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">State Tax Considerations</h4>
                  <p className="text-gray-700 mb-4">
                    We work with your tax advisor to minimize state income taxes, which can be substantial in high-tax states.
                  </p>
                </div>
              </div>
              <div className="mt-6 bg-white rounded-lg p-6 border-2 border-amber-200">
                <p className="text-gray-700">
                  <strong className="text-gray-900">Important:</strong> We're not tax advisors, but we work closely with your CPA or tax attorney to implement the structure that works best for your situation. Our flexibility as direct buyers allows us to accommodate virtually any tax-advantaged structure your advisors recommend.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};